import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";

export type IGeneralLedgerNode = {
  id: string | number;
  code: string;
  name: string;
  node_type?: string;
  children?: IGeneralLedgerNode[];
  opening_balance: number;
  movements: {
    debit: number;
    credit: number;
  };
  net_movement: number;
};

type Props = {
  data: IGeneralLedgerNode[];
  isLoading: boolean;
};

const GeneralLedgerTable: React.FC<Props> = ({ data, isLoading }) => {
  const { t: generalT } = useTranslation("general");

  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(
    {},
  );

  const toggle = (id: string | number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  let totalOpeningBalance = 0;
  let totalDebit = 0;
  let totalCredit = 0;
  let totalNetMovement = 0;

  const renderRows = (
    nodes: IGeneralLedgerNode[],
    depth = 0,
  ): React.ReactNode[] => {
    return nodes.flatMap((node) => {
      const isOpen = expanded[node.id];
      const hasChildren = node.children && node.children.length > 0;

      const openingBalance = node.opening_balance ?? 0;
      const debit = node.movements?.debit ?? 0;
      const credit = node.movements?.credit ?? 0;
      const netMovement = node.net_movement ?? 0;

      totalOpeningBalance += openingBalance;
      totalDebit += debit;
      totalCredit += credit;
      totalNetMovement += netMovement;

      return [
        <TableRow
          key={node.id}
          hover
          sx={{
            bgcolor:
              node.node_type === "category" ? "primary.light" : "transparent",
          }}
        >
          <TableCell width={80}>{node.code}</TableCell>

          <TableCell size="small">
            <Box display="flex" alignItems="center" pl={depth * 3}>
              {hasChildren && (
                <IconButton size="small" onClick={() => toggle(node.id)}>
                  {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              )}
              {!hasChildren && <Box width={32} />}
              {node.name}
            </Box>
          </TableCell>

          <TableCell>{openingBalance.toFixed(2)}</TableCell>
          <TableCell>{debit.toFixed(2)}</TableCell>
          <TableCell>{credit.toFixed(2)}</TableCell>
          <TableCell>{netMovement.toFixed(2)}</TableCell>
        </TableRow>,

        ...(hasChildren && isOpen ? renderRows(node.children!, depth + 1) : []),
      ];
    });
  };

  const tableHeaders = [
    "code",
    "accountName",
    "openingBalance",
    "debit",
    "credit",
    "netMovement",
  ];

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {tableHeaders.map((item) => (
            <TableCell key={item}>
              <Typography
                fontSize={"12px"}
                textTransform={"uppercase"}
                color={"#fff"}
                lineHeight={"18px"}
                fontWeight={500}
              >
                {generalT(item)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton rowsLength={5} cellsLength={6} />
      ) : (
        <TableBody>
          {renderRows(data)}
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{generalT("total")}</TableCell>
            <TableCell />
            <TableCell sx={{ fontWeight: 600 }}>
              {totalOpeningBalance.toFixed(2)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {totalDebit.toFixed(2)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {totalCredit.toFixed(2)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {totalNetMovement.toFixed(2)}
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </Table>
  );
};

export default GeneralLedgerTable;
